import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { Tooltip } from '@neo/primitives/tooltip'
import { Headphones, LogIn, Mic, MicOff, Phone, UserRoundPlus } from 'lucide-react'
import { useMonitorMutations } from 'v2/pages/conversations/mutations/call'
import { isMonitoring } from 'v2/pages/conversations/utils'
import { usePhoneStore } from 'v2/state/phone'
import { ConversationStatus } from 'v2/types/api/conversations'
import { MonitorStates } from 'v2/types/call'
import { phoneSDK } from 'v2/utils/phone-sdk'

interface MainControlsProps {
  isMinimized: boolean
}

export const MainControls = ({ isMinimized }: MainControlsProps) => {
  const { startCoaching, startBarging, leaveMonitoring } = useMonitorMutations()
  const conversation = usePhoneStore((state) => state.callConversation.parentConversation)
  const conversationId = conversation?.conversation_number
  const monitoringState = conversation?.agent_secondary_conversation_state
  const isCoach = monitoringState === MonitorStates.COACH
  const isBarge = monitoringState === MonitorStates.BARGE
  const isMonitor = isMonitoring(monitoringState)
  const isMuted = usePhoneStore((state) => state.isMuted)

  if (!conversationId || !monitoringState) return null

  return (
    <div className={cn('flex items-center gap-3', !isMinimized && isMonitor && '-ml-4')}>
      {isMonitor && (
        <>
          <Button
            onClick={() =>
              startCoaching.mutate({
                conversation_id: conversationId,
                agent_requested_coach: false,
              })
            }
            className="w-28"
            variant="outline"
            disabled={startCoaching.isPending}
            size="sm"
            tooltipContent="Coach - Listen and talk to agent only"
            data-testid="button-coach"
          >
            <Headphones className="h-4 w-4 mr-2" />
            Coach
          </Button>

          <Button
            onClick={() => startBarging.mutate({ conversation_id: conversationId })}
            className="w-28"
            variant="outline"
            size="sm"
            disabled={startBarging.isPending}
            tooltipContent="Barge in - Join the conversation"
            data-testid="button-barge"
          >
            <UserRoundPlus className="h-4 w-4 mr-2" />
            Barge-in
          </Button>
        </>
      )}
      {(isCoach || isBarge) && (
        <Tooltip content={isMuted ? 'Unmute' : 'Mute'}>
          <Button
            variant="tertiary"
            size="iconSm"
            onClick={phoneSDK.toggleMute}
            className={cn(
              'rounded-full',
              isMuted && 'bg-primary text-primary-foreground hover:bg-primary/80',
            )}
            data-testid="button-toggle-mute"
          >
            {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </Button>
        </Tooltip>
      )}
      {isCoach && (
        <Button
          onClick={() => startBarging.mutate({ conversation_id: conversationId })}
          variant="tertiary"
          disabled={startBarging.isPending}
          size="iconSm"
          className="rounded-full"
          tooltipContent="Barge in - Join the conversation"
          data-testid="button-barge-in"
        >
          <LogIn className="h-4 w-4" />
        </Button>
      )}

      <Tooltip content="End monitoring session">
        <Button
          onClick={() =>
            leaveMonitoring.mutate({
              conversationId,
              state: ConversationStatus.CLOSED,
              agent_state: monitoringState,
            })
          }
          disabled={leaveMonitoring.isPending}
          className="rounded-full"
          variant="destructive-outline"
          size="iconSm"
          data-testid="button-end-monitor"
        >
          <Phone className="h-4 w-4 fill-destructive rotate-[135deg]" />
        </Button>
      </Tooltip>
    </div>
  )
}
